import type { JSONObject, JSONValue } from './Assertions.ts'

const PROTOCOL_VERSION = '2025-06-18'
const CORS_HEADERS = {
  'Access-Control-Allow-Origin': '*',
}
const MCP_METHODS = 'POST, GET'

type JsonRpcId = string | number | null
export type McpToolArguments = Record<string, unknown>

export interface McpContext {
  party?: string
  source?: string
  protocol?: string
  sourcePrefix?: string
}

export interface McpToolDefinition<TContext extends McpContext> {
  name: string
  title?: string
  description: string
  inputSchema: JSONValue
  outputSchema?: JSONValue
  annotations?: JSONObject
  capability?: string
  call: (
    args: McpToolArguments,
    context: TContext,
  ) => Promise<JSONValue> | JSONValue
}

export interface McpToolProvider<TContext extends McpContext> {
  listTools(context?: TContext): Promise<JSONValue[]> | JSONValue[]
  callTool(
    name: string,
    args: McpToolArguments,
    context: TContext,
  ): Promise<JSONValue>
}

export interface JsonRpcMcpServerOptions<TContext extends McpContext> {
  serverInfo?: {
    name: string
    version: string
  }
  context: (request: Request) => TContext
}

export class McpToolRegistry<TContext extends McpContext>
  implements McpToolProvider<TContext> {
  #tools: McpToolDefinition<TContext>[]

  constructor(tools: McpToolDefinition<TContext>[]) {
    this.#tools = tools
  }

  listTools(): JSONValue[] {
    return this.#tools.map((tool) => ({
      name: tool.name,
      ...(tool.title === undefined ? {} : { title: tool.title }),
      description: tool.description,
      inputSchema: tool.inputSchema,
      ...(tool.outputSchema === undefined
        ? {}
        : { outputSchema: tool.outputSchema }),
      ...(tool.annotations === undefined
        ? {}
        : { annotations: tool.annotations }),
    }))
  }

  async callTool(
    name: string,
    args: McpToolArguments,
    context: TContext,
  ): Promise<JSONValue> {
    const tool = this.#tools.find((candidate) => candidate.name === name)
    if (!tool) {
      throw new ProtocolError(-32602, `Unknown tool: ${name}`)
    }
    return await tool.call(args, context)
  }
}

export class JsonRpcMcpServer<TContext extends McpContext = McpContext> {
  #tools: McpToolProvider<TContext>
  #context: (request: Request) => TContext
  #serverInfo: { name: string; version: string }

  constructor(
    tools: McpToolProvider<TContext>,
    contextOrOptions:
      | ((request: Request) => TContext)
      | JsonRpcMcpServerOptions<TContext>,
  ) {
    this.#tools = tools
    if (typeof contextOrOptions === 'function') {
      this.#context = contextOrOptions
      this.#serverInfo = {
        name: 'mcp-server',
        version: '1',
      }
    } else {
      this.#context = contextOrOptions.context
      this.#serverInfo = contextOrOptions.serverInfo ?? {
        name: 'mcp-server',
        version: '1',
      }
    }
  }

  async handle(request: Request): Promise<Response> {
    if (request.method === 'GET') {
      return sseEndpointResponse(request)
    }

    if (request.method === 'DELETE') {
      return methodNotAllowed()
    }

    if (request.method !== 'POST') {
      return methodNotAllowed()
    }

    let message: Record<string, unknown>
    try {
      message = await request.json()
    } catch (_e) {
      return jsonResponse(jsonRpcError(null, -32700, 'Parse error'))
    }

    const id = jsonRpcId(message.id)
    if (message.jsonrpc !== '2.0' || typeof message.method !== 'string') {
      return jsonResponse(jsonRpcError(id, -32600, 'Invalid Request'))
    }

    if (message.id === undefined) {
      return accepted()
    }

    try {
      const result = await this.#handleMethod(
        message.method,
        message.params,
        request,
      )
      return jsonResponse({
        jsonrpc: '2.0',
        id,
        result,
      })
    } catch (e) {
      if (e instanceof ProtocolError) {
        return jsonResponse(jsonRpcError(id, e.code, e.message))
      }
      return jsonResponse(jsonRpcError(id, -32603, String(e)))
    }
  }

  async #handleMethod(
    method: string,
    params: unknown,
    request: Request,
  ): Promise<JSONValue> {
    switch (method) {
      case 'initialize':
        return {
          protocolVersion: PROTOCOL_VERSION,
          capabilities: {
            tools: {
              listChanged: false,
            },
          },
          serverInfo: this.#serverInfo,
        }

      case 'notifications/initialized':
        return {}

      case 'tools/list':
        return {
          tools: await this.#tools.listTools(this.#context(request)),
        }

      case 'tools/call':
        return await this.#callTool(params, request)

      default:
        throw new ProtocolError(-32601, `Method not found: ${method}`)
    }
  }

  async #callTool(params: unknown, request: Request): Promise<JSONValue> {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
      throw new ProtocolError(-32602, 'tools/call params must be an object')
    }

    const {
      name,
      arguments: args = {},
    } = params as Record<string, unknown>

    if (
      typeof name !== 'string' || !args || typeof args !== 'object' ||
      Array.isArray(args)
    ) {
      throw new ProtocolError(-32602, 'Invalid tools/call params')
    }

    try {
      const structuredContent = await this.#tools.callTool(
        name,
        args as McpToolArguments,
        this.#context(request),
      )
      return mcpToolResult(structuredContent)
    } catch (e) {
      if (e instanceof ProtocolError) throw e
      return mcpToolResult({ error: String(e) }, true)
    }
  }
}

export class ProtocolError extends Error {
  code: number

  constructor(code: number, message: string) {
    super(message)
    this.code = code
  }
}

export function mcpToolResult(
  structuredContent: JSONValue,
  isError = false,
): JSONValue {
  return {
    content: [
      {
        type: 'text',
        text: typeof structuredContent === 'string'
          ? structuredContent
          : JSON.stringify(structuredContent),
      },
    ],
    structuredContent,
    isError,
  }
}

function jsonRpcError(id: JsonRpcId, code: number, message: string): JSONValue {
  return {
    jsonrpc: '2.0',
    id,
    error: {
      code,
      message,
    },
  }
}

function jsonResponse(value: JSONValue): Response {
  return new Response(JSON.stringify(value), {
    headers: {
      ...CORS_HEADERS,
      'Content-Type': 'application/json',
    },
  })
}

function accepted(): Response {
  return new Response(null, {
    status: 202,
    headers: CORS_HEADERS,
  })
}

function sseEndpointResponse(request: Request): Response {
  const url = new URL(request.url)
  return new Response(`event: endpoint\ndata: ${url.pathname}\n\n`, {
    headers: {
      ...CORS_HEADERS,
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
    },
  })
}

function methodNotAllowed(): Response {
  return new Response(null, {
    status: 405,
    headers: {
      ...CORS_HEADERS,
      'Allow': MCP_METHODS,
    },
  })
}

function jsonRpcId(value: unknown): JsonRpcId {
  return typeof value === 'string' || typeof value === 'number' ||
      value === null
    ? value
    : null
}
